import { mockHeroBanners } from '../../mocks/heroBanners';
import { mockInsights } from '../../mocks/insights';
import { mockServicesContent } from '../../mocks/servicesContent';
import { mockReferenceClients } from '../../mocks/referenceClients';
import { mockLegalPages } from '../../mocks/legalPages';
import { mockClientTypes } from '../../mocks/clientTypes';
import { mockCoreValues } from '../../mocks/coreValues';
import { mockMilestones } from '../../mocks/milestones';
import { mockSeoPages } from '../../mocks/seoData';
import { mockPhotos } from '../../mocks/photos';
import { mockPageHeroes } from '../../mocks/pageHeroes';
import { defaultContactInfo } from '../../mocks/contactInfo';
import { defaultSiteMode } from '../siteMode';

export const STORAGE_KEYS = {
  testimonials: 'zobu_testimonials',
  services: 'zobu_services_v1',
  referenceClients: 'zobu_reference_clients',
  legalPages: 'zobu_legal_pages',
  clientTypes: 'zobu_client_types',
  coreValues: 'zobu_core_values',
  statFounded: 'zobu_stat_founded',
  statYears: 'zobu_stat_years',
  statProjects: 'zobu_stat_projects',
  statClients: 'zobu_stat_clients',
  heroBanners: 'zobu_hero_banners_v2',
  sliderInterval: 'zobu_slider_interval',
  sliderTransition: 'zobu_slider_transition',
  sliderDuration: 'zobu_slider_trans_duration',
  insights: 'zobu_insights',
  insightStats: 'zobu_insight_stats',
  milestones: 'zobu_milestones',
  teamVisible: 'zobu_team_visible',
  adminProjects: 'zobu_admin_projects',
  projectCosts: 'zobu_project_costs',
  timesheets: 'zobu_timesheets',
  timesheetAuditLog: 'zobu_timesheet_audit_log',
  contracts: 'zobu_contracts',
  crmOpportunities: 'zobu_crm_opportunities',
  crmContacts: 'zobu_crm_contacts',
  crmActivities: 'zobu_crm_activities',
  seo: 'zobu_seo',
  jobPositions: 'zobu_job_positions',
  photos: 'zobu_photos',
  contactInfo: 'zobu_contact_info_v1',
  pageHeroes: 'zobu_page_heroes_v1',
  siteMode: 'zobu_site_mode_v1',
  knowledgeBasePublished: 'zobu_knowledge_base_v1',
  knowledgeBaseAdmin: 'zobu_knowledge_base_admin_v1',
  portalProjectsPrefix: 'zobu_portal_projects_',
  portalClientsPrefix: 'zobu_portal_clients_',
  portalInsightsPrefix: 'zobu_portal_insights_',
} as const;

export const PUBLIC_STATE_KEYS = [
  STORAGE_KEYS.testimonials,
  STORAGE_KEYS.services,
  STORAGE_KEYS.referenceClients,
  STORAGE_KEYS.legalPages,
  STORAGE_KEYS.clientTypes,
  STORAGE_KEYS.coreValues,
  STORAGE_KEYS.statFounded,
  STORAGE_KEYS.statYears,
  STORAGE_KEYS.statProjects,
  STORAGE_KEYS.statClients,
  STORAGE_KEYS.heroBanners,
  STORAGE_KEYS.sliderInterval,
  STORAGE_KEYS.sliderTransition,
  STORAGE_KEYS.sliderDuration,
  STORAGE_KEYS.insights,
  STORAGE_KEYS.milestones,
  STORAGE_KEYS.teamVisible,
  STORAGE_KEYS.jobPositions,
  STORAGE_KEYS.photos,
  STORAGE_KEYS.contactInfo,
  STORAGE_KEYS.pageHeroes,
  STORAGE_KEYS.siteMode,
] as const;

export const STAFF_SHARED_READ_KEYS = [
  STORAGE_KEYS.adminProjects,
  STORAGE_KEYS.projectCosts,
  STORAGE_KEYS.timesheets,
  STORAGE_KEYS.knowledgeBasePublished,
] as const;

export const ADMIN_ONLY_KEYS = [
  STORAGE_KEYS.contracts,
  STORAGE_KEYS.timesheetAuditLog,
  STORAGE_KEYS.crmOpportunities,
  STORAGE_KEYS.crmContacts,
  STORAGE_KEYS.crmActivities,
  STORAGE_KEYS.seo,
  STORAGE_KEYS.knowledgeBaseAdmin,
] as const;

export const STAFF_SHARED_WRITE_KEYS = [
  STORAGE_KEYS.projectCosts,
  STORAGE_KEYS.timesheets,
] as const;

const STATIC_KEYS = [
  ...PUBLIC_STATE_KEYS,
  ...STAFF_SHARED_READ_KEYS,
  ...ADMIN_ONLY_KEYS,
] as const;

const STATIC_KEY_SET = new Set<string>(STATIC_KEYS);
const PUBLIC_KEY_SET = new Set<string>(PUBLIC_STATE_KEYS);
const STAFF_SHARED_READ_KEY_SET = new Set<string>(STAFF_SHARED_READ_KEYS);
const ADMIN_ONLY_KEY_SET = new Set<string>(ADMIN_ONLY_KEYS);
const STAFF_SHARED_WRITE_KEY_SET = new Set<string>(STAFF_SHARED_WRITE_KEYS);

export const STATIC_DEFAULT_STATE: Record<string, string> = {
  [STORAGE_KEYS.testimonials]: '[]',
  [STORAGE_KEYS.services]: JSON.stringify(mockServicesContent),
  [STORAGE_KEYS.referenceClients]: JSON.stringify(mockReferenceClients),
  [STORAGE_KEYS.legalPages]: JSON.stringify(mockLegalPages),
  [STORAGE_KEYS.clientTypes]: JSON.stringify(mockClientTypes),
  [STORAGE_KEYS.coreValues]: JSON.stringify(mockCoreValues),
  [STORAGE_KEYS.statFounded]: '2004',
  [STORAGE_KEYS.statYears]: '20+',
  [STORAGE_KEYS.statProjects]: '100+',
  [STORAGE_KEYS.statClients]: '50+',
  [STORAGE_KEYS.heroBanners]: JSON.stringify(mockHeroBanners),
  [STORAGE_KEYS.sliderInterval]: '6500',
  [STORAGE_KEYS.sliderTransition]: 'slide',
  [STORAGE_KEYS.sliderDuration]: '650',
  [STORAGE_KEYS.insights]: '[]',
  [STORAGE_KEYS.milestones]: JSON.stringify(mockMilestones),
  [STORAGE_KEYS.teamVisible]: 'false',
  [STORAGE_KEYS.adminProjects]: '[]',
  [STORAGE_KEYS.projectCosts]: '[]',
  [STORAGE_KEYS.timesheets]: '[]',
  [STORAGE_KEYS.timesheetAuditLog]: '[]',
  [STORAGE_KEYS.contracts]: '[]',
  [STORAGE_KEYS.crmOpportunities]: '[]',
  [STORAGE_KEYS.crmContacts]: '[]',
  [STORAGE_KEYS.crmActivities]: '[]',
  [STORAGE_KEYS.seo]: JSON.stringify(mockSeoPages),
  [STORAGE_KEYS.jobPositions]: '[]',
  [STORAGE_KEYS.photos]: JSON.stringify(mockPhotos),
  [STORAGE_KEYS.contactInfo]: JSON.stringify(defaultContactInfo),
  [STORAGE_KEYS.pageHeroes]: JSON.stringify(mockPageHeroes),
  [STORAGE_KEYS.siteMode]: JSON.stringify(defaultSiteMode),
  [STORAGE_KEYS.knowledgeBasePublished]: '[]',
  [STORAGE_KEYS.knowledgeBaseAdmin]: '[]',
};

export function buildUserScopedDefaults(appUserId: string): Record<string, string> {
  return {
    [`${STORAGE_KEYS.portalProjectsPrefix}${appUserId}`]: '[]',
    [`${STORAGE_KEYS.portalClientsPrefix}${appUserId}`]: '[]',
    [`${STORAGE_KEYS.portalInsightsPrefix}${appUserId}`]: '[]',
  };
}

export function buildUserScopedKeys(appUserId: string): string[] {
  return [
    `${STORAGE_KEYS.portalProjectsPrefix}${appUserId}`,
    `${STORAGE_KEYS.portalClientsPrefix}${appUserId}`,
    `${STORAGE_KEYS.portalInsightsPrefix}${appUserId}`,
  ];
}

export function isPublicStateKey(key: string) {
  return PUBLIC_KEY_SET.has(key);
}

export function isStaffSharedReadKey(key: string) {
  return STAFF_SHARED_READ_KEY_SET.has(key);
}

export function isStaffSharedWriteKey(key: string) {
  return STAFF_SHARED_WRITE_KEY_SET.has(key);
}

export function isAdminOnlyKey(key: string) {
  return ADMIN_ONLY_KEY_SET.has(key);
}

export function isUserScopedKey(key: string) {
  return (
    key.startsWith(STORAGE_KEYS.portalProjectsPrefix) ||
    key.startsWith(STORAGE_KEYS.portalClientsPrefix) ||
    key.startsWith(STORAGE_KEYS.portalInsightsPrefix)
  );
}

export function isTrackedStorageKey(key: string) {
  return STATIC_KEY_SET.has(key) || isUserScopedKey(key);
}

export function shouldSyncStorageKey(key: string) {
  return isTrackedStorageKey(key) && key !== STORAGE_KEYS.insightStats;
}

export function isWritableByAdmin(key: string) {
  return isPublicStateKey(key) || isAdminOnlyKey(key) || key === STORAGE_KEYS.adminProjects;
}

export function getStorageOwnerFromKey(key: string) {
  if (key.startsWith(STORAGE_KEYS.portalProjectsPrefix)) {
    return key.slice(STORAGE_KEYS.portalProjectsPrefix.length);
  }
  if (key.startsWith(STORAGE_KEYS.portalClientsPrefix)) {
    return key.slice(STORAGE_KEYS.portalClientsPrefix.length);
  }
  if (key.startsWith(STORAGE_KEYS.portalInsightsPrefix)) {
    return key.slice(STORAGE_KEYS.portalInsightsPrefix.length);
  }
  return null;
}

export function readStoredJSON<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  const stored = window.localStorage.getItem(key);
  if (!stored) return fallback;
  try {
    return JSON.parse(stored) as T;
  } catch {
    return fallback;
  }
}

export function readStoredString(key: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  return window.localStorage.getItem(key) ?? fallback;
}

export function writeStoredJSON<T>(key: string, value: T) {
  window.localStorage.setItem(key, JSON.stringify(value));
}
